/**
 * "RUN NOW" — the one control that starts a decision pass.
 *
 * ── WHAT IT CANNOT DO, SAID FIRST ──
 *
 * It cannot approve anything, cannot write a decision and cannot change what
 * the policy is allowed to conclude. `POST /partner-integrations/decisions`
 * starts the decision-pass Lambda BY NAME — the same deployed artefact an
 * arriving offer and the daily safety-net rule already start — and changes
 * only WHEN that work happens. What the pass may do is the policy's business,
 * and this button is nowhere near it.
 *
 * ── AND WHY IT NEVER SAYS "DONE" ──
 *
 * The request is answered the moment AWS accepts the invocation, which is
 * before the pass has looked at a single offer: API Gateway's integration
 * timeout is 29 seconds and the pass is sized for 300, so a synchronous
 * version would answer 504 while the work carried on behind it. The honest
 * report is therefore "queued", and the copy below is written so that a
 * reader who takes in only the first half still does not conclude anything
 * was decided. There are no counts here and there cannot be — the request
 * never saw a decision, and a number on this line would be invented.
 *
 * Results arrive by the ordinary read: the pass writes decisions, and Refresh
 * is what picks them up. That is said out loud rather than implied by a
 * spinner that stops.
 *
 * ── THE 409 IS NOT AN ERROR ──
 *
 * `BackgroundRunService` keeps a 60-second cooldown per purpose and states
 * that it is a guard against a double-click, NOT a lock. So a refusal means
 * "one is already running" — which is the thing the clicker wanted — and it
 * is rendered as the same quiet note as a success, not as a failure. An
 * arriving offer takes the same path and holds the same cooldown, so the
 * commonest cause of a 409 here is the system already doing the work.
 *
 * ── WHO SEES IT ──
 *
 * Everyone who can see this screen, because that is exactly how the read
 * beside it is gated. The decisions resource is behind the dashboard Cognito
 * authorizer and NOTHING NARROWER — the handler's own header says so and
 * argues the case, noting that a narrower constant "belongs on the GET and on
 * `sync` in the same change, not on one button". `/partner-sync` carries no
 * `module` in config/navigation.js and no permission on its route, so a UI
 * gate here would hide a working control from the very people who own this
 * queue and can already resolve these offers by hand, while the API went on
 * accepting their POST. A button that 403s beside a screen that loads is the
 * worse answer. See RunDecisionPass.test.jsx, which pins this deliberately.
 */

import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  DECISION_PASS,
  startPartnerDecisionPass,
} from '../../../Services/PartnerIntegrationService';

/**
 * The note after a start. It names `run_id` because that is the one thing
 * that makes the promise checkable — the pass's log lines carry it — and it
 * says what has NOT happened before it says anything else.
 */
export const queuedNote = (runId) => [
  'Pass queued.',
  'Nothing has been decided yet — it takes seconds to a few minutes, and new',
  'decisions show up here when you refresh.',
  runId ? `Run ${runId}.` : null,
].filter(Boolean).join(' ');

/**
 * The note after a refusal, which reports the SAME good news: the work is
 * happening. It never uses the word failed, and it says what to do with the
 * cooldown rather than leaving somebody to guess at it.
 */
export const alreadyRunningNote = ({ startedBy, retryAfterSeconds } = {}) => [
  'A pass is already running —',
  startedBy ? `started by ${startedBy},` : 'started',
  'less than a minute ago, so nothing new was started.',
  'Its decisions will show up here when you refresh.',
  retryAfterSeconds ? `Another can be started in ${retryAfterSeconds}s.` : null,
].filter(Boolean).join(' ');

function RunDecisionPass({ onError }) {
  const [isStarting, setIsStarting] = useState(false);
  const [note, setNote] = useState('');

  const run = async () => {
    setIsStarting(true);
    setNote('');

    try {
      const started = await startPartnerDecisionPass();

      setNote(started.status === DECISION_PASS.ALREADY_RUNNING
        ? alreadyRunningNote(started)
        : queuedNote(started.runId));
    } catch (err) {
      // Anything that is NOT the cooldown. The service has already turned a
      // 409 into an outcome, so reaching here means the pass was not started
      // and the operator has to know.
      if (onError) onError(err.message);
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline-primary"
        size="lg"
        onClick={run}
        disabled={isStarting}
      >
        <i className={`fa fa-play ${isStarting ? 'fa-fade' : ''} me-2`} aria-hidden="true" />
        {isStarting ? 'Starting…' : 'Run now'}
      </Button>

      {/* `role="status"` so the answer is announced: the button's own label
          goes back to "Run now" and would otherwise be the only change a
          screen reader could notice. */}
      {note && (
        <span className="sync-summary sync-summary--muted" role="status">
          {note}
        </span>
      )}
    </>
  );
}

export default RunDecisionPass;
